import { useEffect, useRef, useState, type ReactElement, type RefObject } from 'react';
import { kmText, type KmDay } from '@teslyn/domain';
import { dayLabel, longDay } from './format.ts';

/**
 * Km per day (spec §6.4): a column pair per day, Km flown (`chart-2`) and Accepted km (`chart-1`), the two categorical
 * slots validated with the dataviz validator in both themes. Thin columns with a 4 px rounded top, a 2 px gap between
 * the pair, hairline grid, one axis, the largest accepted day labelled, a legend above the plot, a tooltip on hover
 * and keyboard focus, and the same values in the table below (the chart's text twin).
 */
export interface KmPerDayChartProps {
  readonly days: readonly KmDay[];
  /** "10 Oct 2026 to 16 Oct 2026", for the accessible name. */
  readonly rangeText: string;
}

// SVG units are CSS pixels: the drawing is laid out again for its container's width, so text keeps its size
const DEFAULT_W = 720;
const MIN_W = 280;
const PLOT_H = 220;
const TOP = 20;
const LEFT = 56;
const RIGHT = 8;
const AXIS_H = 28;
const H = TOP + PLOT_H + AXIS_H;
const BAR_MAX = 18;
const GAP = 2;
const RADIUS = 4;

/** Clean ticks from 0 to at least `max`: the smallest step of 1, 2, 2.5 or 5 × 10^n that needs at most five. */
export function niceTicks(max: number): number[] {
  if (!(max > 0)) return [0, 500, 1000];
  const mag = 10 ** Math.floor(Math.log10(max / 5));
  const step = [1, 2, 2.5, 5, 10, 20].map((m) => m * mag).find((s) => Math.ceil(max / s) <= 5)!;
  const ticks: number[] = [];
  for (let i = 0; i <= Math.ceil(max / step); i += 1) ticks.push(i * step);
  return ticks;
}

/** Where the tooltip sits: centred over the day, or held to the side near either edge so it stays in view. */
function tipPlace(x: number, width: number): { left: string; transform: string } {
  const side =
    x < width / 3 ? 'translateX(0)' : x > (2 * width) / 3 ? 'translateX(-100%)' : 'translateX(-50%)';
  return { left: `${x}px`, transform: side };
}

/** Text centred on `x`, or held inside the plot near either end (half of `room` px each side). */
function textAt(
  x: number,
  width: number,
  room: number,
): { x: number; textAnchor: 'start' | 'middle' | 'end' } {
  if (x + room / 2 > width - RIGHT) return { x: width - RIGHT, textAnchor: 'end' };
  if (x - room / 2 < LEFT) return { x: LEFT, textAnchor: 'start' };
  return { x, textAnchor: 'middle' };
}

/** A column with a rounded top and a square foot on the baseline. */
function column(x: number, w: number, y: number, h: number): string {
  const r = Math.min(RADIUS, w / 2, h);
  const b = y + h;
  return `M${x},${b}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${b}Z`;
}

const tickText = (v: number): string => v.toLocaleString('en-AU');

/** The element's width, followed as it changes (DEFAULT_W where it cannot be measured, as in tests). */
function useWidth(): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(DEFAULT_W);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => {
      const width = Math.floor(e?.contentRect.width ?? 0);
      if (width > 0) setW(Math.max(MIN_W, width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

export function KmPerDayChart({ days, rangeText }: KmPerDayChartProps): ReactElement {
  const [active, setActive] = useState<number | null>(null);
  const [frame, W] = useWidth();
  const max = Math.max(0, ...days.flatMap((d) => [d.flownKm, d.acceptedKm]));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1]!;
  const yOf = (v: number): number => TOP + PLOT_H - (v / top) * PLOT_H;
  const plotW = W - LEFT - RIGHT;
  const band = plotW / Math.max(1, days.length);
  // the pair takes at most two thirds of its day, so neighbouring days keep air between them
  const barW = Math.max(1.5, Math.min(BAR_MAX, ((band * 2) / 3 - GAP) / 2));
  // a date under every day when there is room, else every 7th or 14th day; a date that would run off the drawing is
  // left out (the tooltip and the table still have it)
  const every = band >= 72 ? 1 : band * 7 >= 72 ? 7 : 14;
  const LABEL_HALF = 28;
  const labelled = (i: number): boolean => {
    const x = LEFT + (i + 0.5) * band;
    return i % every === 0 && x - LABEL_HALF >= 0 && x + LABEL_HALF <= W;
  };
  const best = days.reduce<number>(
    (b, d, i) => (d.acceptedKm > 0 && (b < 0 || d.acceptedKm > days[b]!.acceptedKm) ? i : b),
    -1,
  );
  const totalFlown = days.reduce((a, d) => a + d.flownKm, 0);
  const totalAccepted = days.reduce((a, d) => a + d.acceptedKm, 0);
  const tip = active === null ? null : days[active];

  return (
    <figure className="chart" aria-label={`Km per day, ${rangeText}`}>
      <ul className="chart__legend caption">
        <li>
          <span className="chart__swatch chart__swatch--flown" aria-hidden="true" />
          Km flown
        </li>
        <li>
          <span className="chart__swatch chart__swatch--accepted" aria-hidden="true" />
          Accepted km
        </li>
      </ul>
      <div className="chart__frame" ref={frame}>
        <svg
          className="chart__svg"
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          role="group"
          aria-label={`Km per day, ${rangeText}: ${kmText(totalFlown)} flown, ${kmText(totalAccepted)} accepted`}
          onPointerLeave={() => setActive(null)}
        >
          <g aria-hidden="true">
            {ticks.map((v) => (
              <g key={v}>
                <line className="chart__grid" x1={LEFT} x2={W - RIGHT} y1={yOf(v)} y2={yOf(v)} />
                <text className="data chart__tick" x={LEFT - 8} y={yOf(v) + 4} textAnchor="end">
                  {tickText(v)}
                </text>
              </g>
            ))}
            <text className="data chart__tick" x={4} y={TOP - 8}>
              km
            </text>
            <line className="chart__axis" x1={LEFT} x2={W - RIGHT} y1={yOf(0)} y2={yOf(0)} />
          </g>
          {days.map((d, i) => {
            const x0 = LEFT + i * band;
            const pairX = x0 + (band - (2 * barW + GAP)) / 2;
            const hf = yOf(0) - yOf(d.flownKm);
            const ha = yOf(0) - yOf(d.acceptedKm);
            const label = `${longDay(d.date)}: ${kmText(d.flownKm)} flown, ${kmText(d.acceptedKm)} accepted, ${d.flights} ${d.flights === 1 ? 'flight' : 'flights'}`;
            return (
              <g
                key={d.date}
                className={active === i ? 'chart__day chart__day--on' : 'chart__day'}
                role="img"
                aria-label={label}
                tabIndex={0}
                onPointerEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
              >
                <rect className="chart__hit" x={x0} y={TOP} width={band} height={PLOT_H + AXIS_H} />
                {hf > 0 && <path className="chart__bar--flown" d={column(pairX, barW, yOf(d.flownKm), hf)} />}
                {ha > 0 && (
                  <path
                    className="chart__bar--accepted"
                    d={column(pairX + barW + GAP, barW, yOf(d.acceptedKm), ha)}
                  />
                )}
                {i === best && (
                  <text
                    className="data chart__value"
                    {...textAt(pairX + barW + GAP + barW / 2, W, 96)}
                    y={yOf(d.acceptedKm) - 6}
                    aria-hidden="true"
                  >
                    {kmText(d.acceptedKm)}
                  </text>
                )}
                {labelled(i) && (
                  <text
                    className="data chart__tick"
                    x={x0 + band / 2}
                    textAnchor="middle"
                    y={TOP + PLOT_H + 18}
                    aria-hidden="true"
                  >
                    {dayLabel(d.date)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {tip && active !== null && (
          <div className="chart__tooltip" role="status" style={tipPlace(LEFT + (active + 0.5) * band, W)}>
            <p className="caption muted">{longDay(tip.date)}</p>
            <p className="caption">
              <span className="chart__key chart__key--flown" aria-hidden="true" />
              <strong className="data">{kmText(tip.flownKm)}</strong>
              <span className="muted">Km flown</span>
            </p>
            <p className="caption">
              <span className="chart__key chart__key--accepted" aria-hidden="true" />
              <strong className="data">{kmText(tip.acceptedKm)}</strong>
              <span className="muted">Accepted km</span>
            </p>
            <p className="caption muted">
              {tip.flights} {tip.flights === 1 ? 'flight' : 'flights'}
              {tip.pendingLines > 0 ? `, ${tip.pendingLines} lines awaiting review` : ''}
            </p>
          </div>
        )}
      </div>
    </figure>
  );
}
